import { useState, useEffect, FormEvent } from "react";
import api from "@/services/api";

interface Project {
  id: string;
  title: string;
}

interface TicketFormState {
  subject: string;
  message: string;
  project_id: string;
  priority: string;
}

type Mode = "create" | "view";

interface Reply {
  id: string;
  message: string;
  author_name: string;
  author_role: string;
  author_job_title: string | null;
  created_at: string;
  is_internal: boolean;
}

interface Ticket {
  id: string;
  subject: string;
  message: string;
  status: string;
  priority: string;
  project_name?: string;
  created_at: string;
  replies: Reply[];
}

interface TicketModalProps {
  open: boolean;
  mode: Mode;
  ticketId?: string;
  onClose: () => void;
  onSubmit?: (data: TicketFormState) => Promise<void>;
  onReply?: (ticketId: string, message: string) => Promise<void>;
  onUpdateStatus?: (ticketId: string, status: string) => Promise<void>;
}

export default function TicketModal({ open, mode, ticketId, onClose, onSubmit, onReply, onUpdateStatus }: TicketModalProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [form, setForm] = useState<TicketFormState>({
    subject: "",
    message: "",
    project_id: "",
    priority: "medium",
  });

  const statusColors: Record<string, string> = {
    open: "text-cyber-orange border-cyber-orange/30 bg-cyber-orange/10",
    in_progress: "text-electric-blue border-electric-blue/30 bg-electric-blue/10",
    resolved: "text-cyber-green border-cyber-green/30 bg-cyber-green/10",
    closed: "text-silver-dim border-dark-border bg-dark-border/30",
  };

  const priorityColors: Record<string, string> = {
    low: "text-silver-dim",
    medium: "text-cyber-orange",
    high: "text-cyber-red",
    urgent: "text-cyber-red",
  };

  useEffect(() => {
    if (open && mode === "create") {
      api.get("/projects").then((res) => setProjects(res.data.data || []));
    }
    if (open && mode === "view" && ticketId) {
      api.get(`/tickets/${ticketId}`).then((res) => setTicket(res.data.data));
    }
  }, [open, mode, ticketId]);

  useEffect(() => {
    if (open && mode === "create") {
      setForm({
        subject: "",
        message: "",
        project_id: projects.length > 0 ? projects[0].id : "",
        priority: "medium",
      });
    }
  }, [open, mode, projects]);

  if (!open) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!onSubmit) return;

    setError(null);
    setLoading(true);

    try {
      const payload = {
        subject: form.subject,
        message: form.message,
        project_id: form.project_id || undefined,
        priority: form.priority,
      };

      await onSubmit(payload);
      onClose();
    } catch (err: any) {
      setError(err instanceof Error ? err.message : "Failed to create ticket");
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (e: FormEvent) => {
    e.preventDefault();
    if (!onReply || !ticketId || !replyMessage.trim()) return;

    try {
      await onReply(ticketId, replyMessage.trim());
      setReplyMessage("");
      // Refresh ticket data
      const res = await api.get(`/tickets/${ticketId}`);
      setTicket(res.data.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleStatusUpdate = async (status: string) => {
    if (!onUpdateStatus || !ticketId) return;

    try {
      await onUpdateStatus(ticketId, status);
      // Refresh ticket data
      const res = await api.get(`/tickets/${ticketId}`);
      setTicket(res.data.data);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-dark-panel border border-dark-border rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slide-up">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {mode === "create" ? "Create Support Ticket" : "Ticket Details"}
            </h3>
            <p className="text-silver text-sm mt-1">
              {mode === "create" ? "Describe your issue and we'll get back to you." : "View ticket details and replies."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-lg border border-dark-border text-silver-dim hover:text-white hover:border-electric-blue/40 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {mode === "create" ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Project (Optional)</span>
                <select
                  className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                  value={form.project_id}
                  onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                >
                  <option value="">General Support</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>{project.title}</option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Priority</span>
                <select
                  className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </label>
            </div>

            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Subject</span>
              <input
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                required
                placeholder="Brief description of the issue"
              />
            </label>

            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Message</span>
              <textarea
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors resize-none"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                rows={5}
                placeholder="Please provide detailed information about your issue..."
              />
            </label>

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl border border-dark-border text-silver hover:text-white hover:border-electric-blue/40 transition-all text-sm font-medium cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${
                  loading ? "bg-electric-blue/40 text-deep-black cursor-not-allowed" : "bg-electric-blue text-deep-black hover:shadow-glow-blue"
                }`}
              >
                {loading ? "Creating..." : "Create Ticket"}
              </button>
            </div>
          </form>
        ) : ticket ? (
          <div className="space-y-6">
            {/* Ticket Info */}
            <div className="bg-dark-card border border-dark-border rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-white font-semibold">{ticket.subject}</h4>
                <div className="flex gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full border ${statusColors[ticket.status]}`}>
                    {ticket.status.replace("_", " ")}
                  </span>
                  <span className={`text-xs font-medium ${priorityColors[ticket.priority]}`}>
                    {ticket.priority}
                  </span>
                </div>
              </div>
              {ticket.project_name && (
                <p className="text-silver-dim text-xs mb-2">Project: {ticket.project_name}</p>
              )}
              <p className="text-silver text-sm">{ticket.message}</p>
              <p className="text-silver-dim text-xs mt-2">
                {new Date(ticket.created_at).toLocaleString()}
              </p>
            </div>

            {/* Status Update for Admins */}
            {onUpdateStatus && (
              <div className="flex gap-2">
                {["open", "in_progress", "resolved", "closed"].map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatusUpdate(status)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                      ticket.status === status
                        ? "bg-electric-blue/20 text-electric-blue"
                        : "bg-dark-card border border-dark-border text-silver-dim hover:text-white hover:border-electric-blue/40"
                    }`}
                  >
                    {status.replace("_", " ")}
                  </button>
                ))}
              </div>
            )}

            {/* Replies */}
            <div className="space-y-3">
              <h4 className="text-white font-semibold text-sm">Replies</h4>
              {ticket.replies.map((reply) => (
                <div key={reply.id} className="bg-dark-card border border-dark-border rounded-xl p-4">
                   <div className="flex items-center gap-2 mb-2">
                     <span className="text-white font-medium text-sm">{reply.author_name}</span>
                     <span className="text-silver-dim text-xs">({reply.author_role})</span>
                     {reply.author_job_title && <span className="text-electric-blue text-xs">({reply.author_job_title})</span>}
                     {!reply.is_internal && <span className="text-electric-blue text-xs">Client Visible</span>}
                   </div>
                  <p className="text-silver text-sm">{reply.message}</p>
                  <p className="text-silver-dim text-xs mt-2">
                    {new Date(reply.created_at).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>

            {/* Add Reply */}
            {ticket.status !== "closed" && (
              <form onSubmit={handleReply} className="space-y-3">
                <label className="block">
                  <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Add Reply</span>
                  <textarea
                    className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors resize-none"
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    required
                    rows={3}
                    placeholder="Type your reply..."
                  />
                </label>
                <button
                  type="submit"
                  className="w-full bg-electric-blue text-deep-black font-semibold py-2.5 rounded-xl hover:shadow-glow-blue transition-all cursor-pointer text-sm"
                >
                  Send Reply
                </button>
              </form>
            )}
          </div>
        ) : (
          <div className="text-center py-10">
            <div className="w-8 h-8 border-2 border-electric-blue border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-silver">Loading ticket details...</p>
          </div>
        )}
      </div>
    </div>
  );
}